/**
 * MechaGuard - Game (游戏世界容器)
 *
 * 负责把地图、敌人、防御塔、投射物、特效与各系统串起来，
 * 并对外暴露统一的接口给 main.js / UI 层调用。
 *
 * 设计约定
 *   - Game 不直接操作 DOM：所有 UI 反馈通过 hooks 回调向外抛出，
 *     这样 UI 与逻辑解耦，后续换 UI 框架不需要改游戏逻辑。
 *   - 所有伤害都经过 damageEnemy()，护甲计算、击杀奖励、死亡特效
 *     只在这一处实现。
 */

import {
  createMap,
  worldToTile,
  TILE_SIZE,
  DEFAULT_LEVEL_ID,
  getLevel,
} from '../maps/MapData.js';
import { MapRenderer } from '../maps/MapRenderer.js';
import { Enemy } from '../entities/Enemy.js';
import { Projectile } from '../entities/Projectile.js';
import { getEnemyConfig } from '../enemy-types/index.js';
import { balanceConfig, GLOBAL_DEFAULTS } from '../config/BalanceConfig.js';
import { createTower, getTowerMeta } from '../tower-types/index.js';
import { EconomySystem } from '../systems/EconomySystem.js';
import { ParticleSystem } from '../systems/ParticleSystem.js';
import { EffectSystem } from '../systems/EffectSystem.js';
import { WaveSystem, buildWaves } from '../systems/WaveSystem.js';
import {
  SkillSystem,
  ENERGY_PER_WAVE,
  ENERGY_PER_BOSS,
} from '../systems/SkillSystem.js';
import { AudioSystem } from '../systems/AudioSystem.js';
import { AchievementSystem } from '../systems/AchievementSystem.js';
import { LeaderboardSystem, computeScore } from '../systems/LeaderboardSystem.js';
import { createStorage } from '../systems/Storage.js';

/** 游戏进行阶段 */
export const GamePhase = Object.freeze({
  PLAYING: 'playing',
  VICTORY: 'victory',
  DEFEAT: 'defeat',
});

/** 提前召唤奖励：每跳过 1 秒给多少金币 */
const EARLY_CALL_GOLD_PER_SEC = 3;

/* ---- 技能效果参数（运营可在 ?ops=1 面板调；以下为未初始化时的回退值） ---- */
const EMP_FREEZE_DURATION = GLOBAL_DEFAULTS.empFreezeDuration;
const SHIELD_AMOUNT = GLOBAL_DEFAULTS.shieldAmount;
const ORBITAL_RADIUS = GLOBAL_DEFAULTS.orbitalRadius;
const ORBITAL_DAMAGE = GLOBAL_DEFAULTS.orbitalDamage;

export class Game {
  /**
   * @param {object} opts
   * @param {import('./Renderer.js').Renderer} opts.renderer
   * @param {object} [opts.hooks]
   * @param {string} [opts.levelId] 关卡 id，缺省为前哨站
   * @param {{getItem:Function,setItem:Function,removeItem:Function}} [opts.storage]
   */
  constructor({ renderer, hooks = {}, levelId = DEFAULT_LEVEL_ID, storage = null }) {
    this.renderer = renderer;
    this.hooks = hooks;
    this.levelId = levelId;

    // 持久化后端（缺省自动探测 localStorage，不可用时退化为内存）
    const resolved = storage || createStorage().backend;
    this.storage = resolved;

    this.economy = new EconomySystem(200);
    this.particles = new ParticleSystem(500);
    this.effects = new EffectSystem();

    // 音频：构造时不创建 AudioContext（浏览器要求用户手势后才能创建），
    // 由 main.js 在首次交互时调 audio.unlock()
    this.audio = new AudioSystem({ storage: resolved });

    // 成就：解锁时同时弹提示与音效
    this.achievements = new AchievementSystem({
      storage: resolved,
      onUnlock: (achievement) => {
        this.hooks.onAchievement?.(achievement);
        this.audio.play('achievement');
      },
    });

    // 本地排行榜
    this.leaderboard = new LeaderboardSystem({ storage: resolved });

    /** 本局结算结果（分数 / 名次），供结算界面读取 */
    this.lastRunResult = null;

    /** 成就评估节流计时 */
    this._achTimer = 0;
    /** 音乐模式节流计时 */
    this._musicTimer = 0;

    // 技能系统只做资源账本，效果实现留给 Game。
    //
    // 重要：SkillSystem 的构造函数内部会调用 reset()，从而**立即**触发一次
    // onEnergyChange —— 那一刻 `this.skills = new SkillSystem(...)` 的赋值
    // 还没完成，this.skills 仍是 undefined。因此回调里必须先做存在性检查，
    // 不能直接读 this.skills.energy。
    this.skills = new SkillSystem({
      onEnergyChange: () => {
        const skills = this.skills;
        if (!skills) return; // 构造期间的首次回调，等 reset() 再同步
        this.hooks.onEnergyChange?.(skills.energy, skills.maxEnergy);
      },
      onCooldownChange: () => {
        this.hooks.onSkillStateChange?.();
      },
    });

    /** 建造预览用的幽灵塔缓存 */
    this._ghost = null;
    this._ghostType = null;

    this.reset();
  }

  /* ===================================================
   * 音效转发
   * =================================================== */

  /**
   * 播放音效。塔 / 投射物 / 敌人都通过这个入口发声，
   * 这样它们不需要知道 AudioSystem 的存在，也不怕音频不可用。
   * @param {string} name
   */
  sfx(name) {
    this.audio.play(name);
  }

  /* ===================================================
   * 生命周期
   * =================================================== */

  /**
   * 重置整局游戏（开始/重开/换关卡都走这里）
   * @param {string} [levelId] 不传则沿用当前关卡
   */
  reset(levelId = this.levelId) {
    this.levelId = levelId;
    this.level = getLevel(levelId);

    this.map = createMap(this.levelId);
    this.mapRenderer = new MapRenderer(this.map);

    /** @type {import('../entities/Tower.js').Tower[]} */
    this.towers = [];
    /** @type {Map<string, import('../entities/Tower.js').Tower>} */
    this.towerGrid = new Map();

    /** @type {import('../entities/Enemy.js').Enemy[]} */
    this.enemies = [];
    /** @type {import('../entities/Projectile.js').Projectile[]} */
    this.projectiles = [];

    this.particles.clear();
    this.effects.clear();
    this._ghost = null;
    this._ghostType = null;

    // 关卡决定初始生命与难度
    this.lives = this.level.startLives;
    this.startLives = this.level.startLives;
    this.phase = GamePhase.PLAYING;

    /** 护盾点数：漏怪时优先消耗，不扣生命 */
    this.shield = 0;

    this.buildType = null;
    this.selectedTower = null;
    this.hoverTile = null;
    /** 鼠标当前的世界坐标（技能瞄准用，比格子更精确） */
    this.hoverWorld = null;
    /** 正在瞄准地图位置的技能 id（如轨道打击） */
    this.pendingSkill = null;

    // 统计
    this.kills = 0;
    this.elapsed = 0;
    this.wavesCleared = 0;

    // HUD 同步缓存
    this._lastWave = -1;
    this._lastPrep = -2;
    this._lastLives = -1;

    // 经济（先注册回调，再设置初始值，确保首帧就能同步到 UI）
    this.economy.onChange = (gold) => {
      this.hooks.onGoldChange?.(gold);
      // 「富可敌国」成就需要追踪本局金币峰值
      this.achievements.runSet('gold', gold);
      this.achievements.runMax('goldPeak', gold);
    };
    this.economy.set(this.level.startGold);

    // 技能资源
    this.skills.reset();

    // 波次
    this.waveSystem = new WaveSystem({
      waves: buildWaves(this.level.totalWaves, this.level.hpScaleMul, balanceConfig.getWaveOptions()),
      onSpawn: (typeId, hpScale) => this.spawnEnemy(typeId, hpScale),
      onWaveStart: (waveNumber) => {
        this.sfx('waveStart');
        this.hooks.onToast?.(`第 ${waveNumber} 波来袭`);
      },
      onWaveComplete: (waveNumber, bonus) => {
        this.wavesCleared = waveNumber;
        if (bonus > 0) this.economy.earn(bonus);

        // 每清空一波补充能量
        this.skills.addEnergy(ENERGY_PER_WAVE);

        this.sfx('waveClear');
        this.achievements.runSet('wavesCleared', waveNumber);
        this.achievements.bump('totalWavesCleared');

        this.hooks.onToast?.(`第 ${waveNumber} 波已肃清  +${bonus}G  ⚡+${ENERGY_PER_WAVE}`);
      },
      onAllComplete: () => this._victory(),
    });
    this.waveSystem.begin();

    // 首帧同步一次全部 UI 状态
    this._emitLives(true);
    this._syncHud(true);
    this.hooks.onGoldChange?.(this.economy.gold);
    this.hooks.onBuildTypeChange?.(null);
    this.hooks.onSelectionChange?.(null);
    this.hooks.onShieldChange?.(this.shield);
    this.hooks.onPendingSkillChange?.(null);
    this.hooks.onLevelChange?.(this.level);

    // 本局结算结果清空；成就的单局统计清零（累计统计保留）
    this.lastRunResult = null;
    this._achTimer = 0;
    this._musicTimer = 0;
    this.achievements.startRun({
      levelId: this.levelId,
      difficulty: this.level.difficulty,
      lives: this.lives,
      gold: this.economy.gold,
    });

    // 相机适配整张地图
    this.renderer.fitToBounds(this.map.width, this.map.height);

    // 进入战斗配乐（备战阶段也放战斗曲，营造压迫感）
    this._updateMusicMode();
  }

  /* ===================================================
   * 主循环
   * =================================================== */

  /** @param {number} dt 秒 */
  update(dt) {
    if (this.phase !== GamePhase.PLAYING) {
      // 结束后仍推进特效，让爆炸动画播完
      this.effects.update(dt);
      this.particles.update(dt);
      return;
    }

    this.elapsed += dt;
    this.mapRenderer.update(dt);
    this.skills.update(dt);

    // 成就评估与音乐模式切换（内部各自节流）
    this._tickProgressSystems(dt);

    // ---- 1. 波次推进 ----
    this.waveSystem.update(dt, this.aliveEnemyCount);

    // 波次系统可能在回调里直接判定胜利，此时不再推进世界
    if (this.phase !== GamePhase.PLAYING) {
      this.effects.update(dt);
      this.particles.update(dt);
      this._cleanup();
      return;
    }

    // ---- 2. 敌人移动 / 漏怪判定 ----
    const enemies = this.enemies;
    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (!e.alive) continue;

      e.update(dt);

      if (e.reachedBase) {
        e.alive = false;
        this._onEnemyLeak(e);
        if (this.phase !== GamePhase.PLAYING) break;
      }
    }

    // ---- 3. 防御塔开火 ----
    const towers = this.towers;
    for (let i = 0; i < towers.length; i++) {
      towers[i].update(dt, this);
      if (this.phase !== GamePhase.PLAYING) break;
    }

    // ---- 4. 投射物 ----
    if (this.phase === GamePhase.PLAYING) {
      const projectiles = this.projectiles;
      for (let i = 0; i < projectiles.length; i++) {
        projectiles[i].update(dt, this);
      }
    }

    // ---- 5. 表现层 ----
    this.effects.update(dt);
    this.particles.update(dt);

    // ---- 6. 清理 ----
    this._cleanup();

    // ---- 7. HUD 同步 ----
    this._syncHud(false);
  }

  /* ===================================================
   * 成就 / 音乐：节流推进
   * =================================================== */

  /**
   * 成就谓词有 20 条、音乐模式要扫一遍敌人找 BOSS，
   * 两者都不必每帧跑，各自做节流。
   */
  _tickProgressSystems(dt) {
    this._achTimer -= dt;
    if (this._achTimer <= 0) {
      this._achTimer = 0.4;
      this.achievements.evaluate();
    }

    this._musicTimer -= dt;
    if (this._musicTimer <= 0) {
      this._musicTimer = 0.5;
      this._updateMusicMode();
    }
  }

  /** 场上有 BOSS 时切到更紧张的配乐 */
  _updateMusicMode() {
    if (this.phase !== GamePhase.PLAYING) {
      this.audio.setMusicMode('menu');
      return;
    }

    let bossActive = false;
    for (let i = 0; i < this.enemies.length; i++) {
      const e = this.enemies[i];
      if (e.alive && e.typeId === 'boss') {
        bossActive = true;
        break;
      }
    }

    this.audio.setMusicMode(bossActive ? 'boss' : 'combat');
  }

  /** 绘制整个世界（相机变换由本方法内部处理） */
  render() {
    const renderer = this.renderer;
    const ctx = renderer.ctx;

    renderer.clear('#050a14');
    renderer.applyCamera();

    // 地面与此前的地图元素
    this.mapRenderer.draw(ctx);

    // 建造高亮（贴地，画在实体下方）
    this._drawTileHighlight(ctx);

    // 实体
    const enemies = this.enemies;
    for (let i = 0; i < enemies.length; i++) enemies[i].draw(ctx);

    const towers = this.towers;
    for (let i = 0; i < towers.length; i++) {
      towers[i].draw(ctx, { selected: towers[i] === this.selectedTower });
    }

    // 建造预览（幽灵塔 + 射程圈）
    this._drawBuildGhost(ctx);

    // 技能瞄准圈
    this._drawSkillReticle(ctx);

    const projectiles = this.projectiles;
    for (let i = 0; i < projectiles.length; i++) projectiles[i].draw(ctx);

    this.effects.draw(ctx);
    this.particles.draw(ctx);

    renderer.resetTransform();
  }

  /* ===================================================
   * 输入
   * =================================================== */

  /** 单击（点击/轻触） */
  handleTap(wx, wy) {
    if (this.phase !== GamePhase.PLAYING) return;

    // 技能瞄准模式优先：此时点击地图 = 指定技能落点
    if (this.pendingSkill) {
      this.hoverWorld = { x: wx, y: wy };
      this.castSkill(this.pendingSkill, { x: wx, y: wy });
      return;
    }

    const { col, row } = worldToTile(wx, wy);

    // 触屏没有 hover 事件，这里同步一次坐标，
    // 让手机端在建造时同样能显示格子高亮与幽灵塔预览。
    this.hoverTile = { col, row };

    const existing = this.towerAt(col, row);

    // 建造模式
    if (this.buildType) {
      if (existing) {
        // 点到已有塔：切换为选中它
        this._setBuildType(null);
        this._selectTower(existing);
        return;
      }
      this._tryBuild(col, row);
      return;
    }

    // 选择模式
    this._selectTower(existing || null);
  }

  /** 右键 / 长按：取消当前操作 */
  handleSecondary() {
    // 优先级：取消技能瞄准 > 退出建造 > 取消选中
    if (this.pendingSkill) {
      this._setPendingSkill(null);
      return;
    }
    if (this.buildType) {
      this._setBuildType(null);
      return;
    }
    if (this.selectedTower) {
      this._selectTower(null);
    }
  }

  /** 鼠标悬停（仅桌面端有效） */
  handleHover(wx, wy) {
    if (this.phase !== GamePhase.PLAYING) return;

    // 技能瞄准需要精确的世界坐标（不受格子对齐限制）
    this.hoverWorld = { x: wx, y: wy };

    const { col, row } = worldToTile(wx, wy);
    if (!this.hoverTile || this.hoverTile.col !== col || this.hoverTile.row !== row) {
      this.hoverTile = { col, row };
    }
  }

  /* ===================================================
   * 玩家操作
   * =================================================== */

  /**
   * 选择要建造的塔类型
   * @param {string|null} id
   */
  setBuildType(id) {
    if (id === null) {
      this._setBuildType(null);
      return;
    }

    const meta = getTowerMeta(id);
    if (!meta) return;

    if (!this.economy.canAfford(balanceConfig.getTowerCost(id, meta.cost))) {
      this.sfx('error');
      this.hooks.onToast?.('金币不足');
      return;
    }

    this.sfx('click');
    this._setBuildType(id);
    this._selectTower(null);
  }

  /** 召唤下一波（备战期可提前） */
  /** 运营视图调整后重建波次表（对下一波生效） */
  rebuildWaves() {
    if (!this.waveSystem) return;
    const opts = balanceConfig.getWaveOptions();
    this.waveSystem.waves = buildWaves(this.level.totalWaves, this.level.hpScaleMul, opts);
    if (typeof opts.prepTime === 'number') this.waveSystem.prepTime = opts.prepTime;
  }

  callNextWave() {
    if (this.phase !== GamePhase.PLAYING) return;

    const remaining = this.waveSystem.callNextWave();
    if (remaining <= 0) return;

    const bonus = Math.floor(remaining * EARLY_CALL_GOLD_PER_SEC);
    if (bonus > 0) {
      this.economy.earn(bonus);
      this.hooks.onToast?.(`提前召唤奖励 +${bonus}G`);
    }
  }

  /** 升级当前选中的塔 */
  upgradeSelected() {
    const t = this.selectedTower;
    if (!t) return;

    if (!t.canUpgrade()) {
      this.sfx('error');
      this.hooks.onToast?.('已达到最高等级');
      return;
    }

    const cost = t.getUpgradeCost();
    if (!this.economy.canAfford(cost)) {
      this.sfx('error');
      this.hooks.onToast?.('金币不足');
      return;
    }

    this.economy.spend(cost);
    t.upgrade();

    this.sfx('upgrade');
    this.achievements.runMax('maxTowerLevel', t.level);

    this.particles.spawnBurst(t.x, t.y, {
      count: 16,
      color: t.color,
      speed: 140,
      life: 0.5,
      size: 2.6,
    });
    this.effects.addRing(t.x, t.y, t.range, t.color, 0.5);

    this.hooks.onSelectionChange?.(t); // 刷新面板数值
  }

  /** 出售当前选中的塔 */
  sellSelected() {
    const t = this.selectedTower;
    if (!t) return;

    const value = t.getSellValue();
    this.economy.earn(value);
    this._removeTower(t);
    this._selectTower(null);

    this.sfx('sell');
    this.hooks.onToast?.(`已出售 ${t.name}  +${value}G`);
  }

  /* ===================================================
   * 特殊技能
   * =================================================== */

  /**
   * 释放技能（UI 唯一入口）
   *
   * 对需要指定位置的技能，分两步：
   *   第一次调用（不带 target）→ 进入瞄准模式，等待玩家点地图；
   *   第二次调用（带 target）  → 真正结算效果。
   *
   * @param {string} id
   * @param {{x:number,y:number}|null} [target]
   */
  castSkill(id, target = null) {
    if (this.phase !== GamePhase.PLAYING) return;

    const skill = this.skills.getSkill(id);
    if (!skill) return;

    // ---- 指向型技能：先进入瞄准模式 ----
    if (skill.targeting === 'point' && !target) {
      if (this.pendingSkill === id) {
        this._setPendingSkill(null); // 再点一次同一个技能 = 取消
        return;
      }

      const check = this.skills.canCast(id);
      if (!check.ok) {
        this.sfx('error');
        this.hooks.onToast?.(check.reason);
        return;
      }

      // 进入瞄准时退出建造/选中，避免操作语义混淆
      this.sfx('click');
      this._setBuildType(null);
      this._selectTower(null);
      this._setPendingSkill(id);
      this.hooks.onToast?.(`选择${skill.name}的目标位置（右键取消）`);
      return;
    }

    // ---- 结算 ----
    const check = this.skills.canCast(id);
    if (!check.ok) {
      this.sfx('error');
      this.hooks.onToast?.(check.reason);
      return;
    }

    // 效果没打中任何目标时不消耗能量，避免「空放」的挫败感
    if (!this._applySkillEffect(id, target)) return;

    this.skills.consume(id);
    this._setPendingSkill(null);

    this.achievements.runAdd('skillsCast');
    this.achievements.bump('totalSkills');
  }

  /**
   * 技能实际效果
   * @returns {boolean} 是否真正生效（false 则不消耗能量）
   */
  _applySkillEffect(id, target) {
    // 实时读取运营覆盖（未覆盖时回退到 GLOBAL_DEFAULTS）
    const g = balanceConfig.getGlobal();
    const orbitalRadius = Number.isFinite(g.orbitalRadius) ? g.orbitalRadius : ORBITAL_RADIUS;
    const orbitalDamage = Number.isFinite(g.orbitalDamage) ? g.orbitalDamage : ORBITAL_DAMAGE;
    const empFreeze = Number.isFinite(g.empFreezeDuration) ? g.empFreezeDuration : EMP_FREEZE_DURATION;
    const shieldAmt = Number.isFinite(g.shieldAmount) ? g.shieldAmount : SHIELD_AMOUNT;

    switch (id) {
      // ---- EMP：冻结全场敌人 ----
      case 'emp': {
        let hit = 0;

        for (let i = 0; i < this.enemies.length; i++) {
          const e = this.enemies[i];
          if (!e.alive || e.reachedBase) continue;
          e.applyFreeze(empFreeze);
          hit += 1;
        }

        if (hit === 0) {
          this.hooks.onToast?.('场上没有敌人，EMP 未消耗');
          return false;
        }

        this.sfx('skillEmp');

        // 以地图中心为圆心的全屏冲击环
        this.effects.addRing(
          this.map.width / 2,
          this.map.height / 2,
          Math.hypot(this.map.width, this.map.height) * 0.55,
          '#00ffff',
          0.7
        );
        this.hooks.onToast?.(`EMP 冲击波：冻结 ${hit} 个敌人`);
        return true;
      }

      // ---- 应急护盾 ----
      case 'shield': {
        this.shield += shieldAmt;
        this.sfx('skillShield');
        this.hooks.onShieldChange?.(this.shield);
        this.effects.addRing(this.map.base.x, this.map.base.y, 130, '#00ff88', 0.8);
        this.hooks.onToast?.(`应急护盾生效（护盾 ${this.shield} 点）`);
        return true;
      }

      // ---- 轨道打击 ----
      case 'orbital': {
        if (!target) return false;

        const hits = this.damageArea(
          target.x,
          target.y,
          orbitalRadius,
          orbitalDamage,
          { label: '轨道打击' }
        );

        if (hits === 0) {
          this.hooks.onToast?.('该位置没有敌人，轨道打击未消耗');
          return false;
        }

        this.sfx('skillOrbital');
        this.achievements.runMax('orbitalMaxHits', hits);
        this.hooks.onToast?.(`轨道打击命中 ${hits} 个目标`);
        return true;
      }

      default:
        return false;
    }
  }

  /**
   * 对圆形区域内的敌人造成伤害
   * @param {number} x
   * @param {number} y
   * @param {number} radius
   * @param {number} damage
   * @returns {number} 命中数量
   */
  damageArea(x, y, radius, damage, opts = {}) {
    const r2 = radius * radius;
    let hits = 0;

    // 遍历副本：damageEnemy 可能触发击杀并改变数组内容
    const targets = this.enemies.slice();
    for (let i = 0; i < targets.length; i++) {
      const e = targets[i];
      if (!e.alive || e.reachedBase) continue;
      if (e.distanceSqTo(x, y) > r2) continue;

      this.damageEnemy(e, damage, opts);
      hits += 1;
    }

    this.effects.addExplosion(x, y, radius, '#ff9900', 0.7);
    this.particles.spawnBurst(x, y, {
      count: 30,
      color: '#ff9900',
      speed: 260,
      life: 0.7,
      size: 3.6,
    });

    return hits;
  }

  /* ===================================================
   * 世界操作（供塔 / 投射物调用）
   * =================================================== */

  /**
   * 生成敌人
   * @param {string} typeId
   * @param {number} [hpScale=1]
   */
  spawnEnemy(typeId, hpScale = 1) {
    const config = getEnemyConfig(typeId);
    const enemy = new Enemy({
      typeId,
      config,
      waypoints: this.map.waypoints,
      hpScale,
      speedMul: this.level.speedMul,
      onPhaseChange: (e, phase) => this._onEnemyPhaseChange(e, phase),
    });
    this.enemies.push(enemy);
    return enemy;
  }

  /** BOSS 进入新形态时的表现与提示 */
  _onEnemyPhaseChange(enemy, phase) {
    const color = phase.color || enemy.color;

    this.sfx('bossPhase');
    this.achievements.runMax('bossPhaseMax', enemy.phaseIndex);

    this.effects.addExplosion(enemy.x, enemy.y, enemy.radius * 3.2, color, 0.6);
    this.particles.spawnBurst(enemy.x, enemy.y, {
      count: 26,
      color,
      speed: 210,
      life: 0.65,
      size: 3.4,
    });

    this.hooks.onToast?.(`${enemy.name} 进入「${phase.name}」形态`);
  }

  /** 生成投射物 */
  spawnProjectile(opts) {
    const p = new Projectile(opts);
    this.projectiles.push(p);
    return p;
  }

  /**
   * 统一的伤害入口：处理护甲、击杀奖励与死亡特效
   * @param {import('../entities/Enemy.js').Enemy} enemy
   * @param {number} amount
   * @param {{ignoreArmor?:number}} [opts]
   * @returns {number} 实际造成的伤害
   */
  damageEnemy(enemy, amount, opts = {}) {
    if (!enemy || !enemy.alive) return 0;

    const dealt = enemy.takeDamage(amount, opts);

    if (!enemy.alive) {
      this.kills += 1;
      this.economy.earn(enemy.reward);

      const isBoss = enemy.typeId === 'boss';

      this.sfx('enemyDeath');
      this.achievements.bump('totalKills');

      this.particles.spawnBurst(enemy.x, enemy.y, {
        count: isBoss ? 30 : 12,
        color: enemy.color,
        speed: isBoss ? 240 : 170,
        life: 0.55,
        size: 3,
      });

      if (isBoss) {
        this.sfx('explosion');
        this.achievements.bump('totalBossKills');

        this.effects.addExplosion(enemy.x, enemy.y, 96, enemy.color, 0.65);
        // 击杀 BOSS 额外补充能量
        this.skills.addEnergy(ENERGY_PER_BOSS);
        this.hooks.onToast?.(`BOSS 已摧毁  +${enemy.reward}G  ⚡+${ENERGY_PER_BOSS}`);
      }
    }

    return dealt;
  }

  /**
   * 取射程内的所有存活敌人
   * @returns {import('../entities/Enemy.js').Enemy[]}
   */
  enemiesInRange(x, y, range) {
    const r2 = range * range;
    const out = [];

    for (let i = 0; i < this.enemies.length; i++) {
      const e = this.enemies[i];
      if (!e.alive || e.reachedBase) continue;
      if (e.distanceSqTo(x, y) <= r2) out.push(e);
    }

    return out;
  }

  /** 指定格上的塔 */
  towerAt(col, row) {
    return this.towerGrid.get(`${col},${row}`) || null;
  }

  /** 场上存活敌人数 */
  get aliveEnemyCount() {
    let n = 0;
    for (let i = 0; i < this.enemies.length; i++) {
      const e = this.enemies[i];
      if (e.alive && !e.reachedBase) n += 1;
    }
    return n;
  }

  /** 结算数据 */
  getStats() {
    const run = this.lastRunResult;

    return {
      levelId: this.levelId,
      levelName: this.level.name,
      difficulty: this.level.difficulty,
      kills: this.kills,
      lives: this.lives,
      shield: this.shield,
      wavesCleared: this.wavesCleared,
      totalWaves: this.waveSystem.totalWaves,
      gold: this.economy.gold,
      towers: this.towers.length,
      elapsed: this.elapsed,

      // 计分与排行
      score: run ? run.score : 0,
      rank: run ? run.rank : null,
      isBest: run ? run.isBest : false,
      onBoard: run ? run.onBoard : false,
      /** 是否参与排行榜；自定义地图为 false */
      ranked: run ? run.ranked !== false : this.level.custom !== true,

      // 成就进度
      achievementsUnlocked: this.achievements.unlockedCount,
      achievementsTotal: this.achievements.total,
    };
  }

  /* ===================================================
   * 内部：建造 / 选择
   * =================================================== */

  _setBuildType(id) {
    if (this.buildType === id) return;
    this.buildType = id;
    this.hooks.onBuildTypeChange?.(id);
  }

  _setPendingSkill(id) {
    if (this.pendingSkill === id) return;
    this.pendingSkill = id;
    this.hooks.onPendingSkillChange?.(id);
  }

  _selectTower(tower) {
    if (this.selectedTower === tower) return;
    this.selectedTower = tower;
    this.hooks.onSelectionChange?.(tower);
  }

  _tryBuild(col, row) {
    const id = this.buildType;
    const meta = getTowerMeta(id);
    if (!meta) return;

    if (!this.map.isBuildable(col, row)) {
      this.sfx('error');
      this.hooks.onToast?.('这里无法建造（敌人通道或越界）');
      return;
    }

    if (this.towerAt(col, row)) {
      this.sfx('error');
      this.hooks.onToast?.('该位置已有防御塔');
      return;
    }

    if (!this.economy.canAfford(balanceConfig.getTowerCost(id, meta.cost))) {
      this.sfx('error');
      this.hooks.onToast?.('金币不足');
      this._setBuildType(null);
      return;
    }

    this.economy.spend(balanceConfig.getTowerCost(id, meta.cost));

    const tower = createTower(id, col, row);
    this.towers.push(tower);
    this.towerGrid.set(`${col},${row}`, tower);

    this.sfx('build');
    this.achievements.runAdd('towersBuilt');
    this.achievements.runMax('maxTowerLevel', tower.level);

    this.particles.spawnBurst(tower.x, tower.y, {
      count: 12,
      color: tower.color,
      speed: 120,
      life: 0.4,
      size: 2.4,
    });

    // 金币不够再造一座时自动退出建造模式
    if (!this.economy.canAfford(balanceConfig.getTowerCost(id, meta.cost))) {
      this._setBuildType(null);
    }
  }

  _removeTower(tower) {
    const i = this.towers.indexOf(tower);
    if (i >= 0) this.towers.splice(i, 1);
    this.towerGrid.delete(`${tower.col},${tower.row}`);
    if (this.selectedTower === tower) this._selectTower(null);
  }

  /* ===================================================
   * 内部：结算
   * =================================================== */

  _onEnemyLeak(enemy) {
    // 护盾优先吸收本次突破
    if (this.shield > 0) {
      this.shield -= 1;

      this.sfx('skillShield');
      this.achievements.runAdd('shieldAbsorbs');

      this.effects.addRing(enemy.x, enemy.y, 68, '#00ff88', 0.45);
      this.particles.spawnBurst(enemy.x, enemy.y, {
        count: 16,
        color: '#00ff88',
        speed: 190,
        life: 0.5,
        size: 3,
      });

      this.hooks.onShieldChange?.(this.shield);
      this.hooks.onToast?.(`护盾吸收了这次突破（护盾剩余 ${this.shield}）`);
      return;
    }

    this.lives = Math.max(0, this.lives - 1);
    this.sfx('leak');
    this.achievements.runAdd('livesLost');

    this.particles.spawnBurst(enemy.x, enemy.y, {
      count: 18,
      color: '#ff3366',
      speed: 200,
      life: 0.6,
      size: 3.2,
    });
    this.effects.addExplosion(enemy.x, enemy.y, 70, '#ff3366', 0.45);

    this._emitLives(false);
    this.hooks.onToast?.(`漏怪！生命 -1（剩余 ${this.lives}）`);

    if (this.lives <= 0) this._defeat();
  }

  _victory() {
    if (this.phase !== GamePhase.PLAYING) return;
    this.phase = GamePhase.VICTORY;

    this.sfx('victory');
    this._finalizeRun(true);

    this._syncHud(true); // 保证结算界面出现前 HUD 处于最终状态
    this._updateMusicMode();
    this.hooks.onVictory?.(this.getStats());
  }

  _defeat() {
    if (this.phase !== GamePhase.PLAYING) return;
    this.phase = GamePhase.DEFEAT;

    this.sfx('defeat');
    this._finalizeRun(false);

    this._syncHud(true);
    this._updateMusicMode();
    this.hooks.onDefeat?.(this.getStats());
  }

  /**
   * 本局结算：写入成就统计、计算分数、提交排行榜。
   *
   * 胜利与失败都会走这里；中途「退出到主菜单」不算完成一局，不记录成绩。
   */
  _finalizeRun(victory) {
    const achievements = this.achievements;

    achievements.runSet('victory', victory);
    achievements.runSet('finished', true);
    achievements.runSet('elapsed', this.elapsed);
    achievements.runSet('wavesCleared', this.wavesCleared);
    achievements.runSet('lives', this.lives);
    achievements.runSet('gold', this.economy.gold);

    achievements.bump('totalGames');
    if (victory) {
      achievements.bump('totalVictories');
      achievements.recordLevelWin(this.levelId);
    }

    const score = computeScore({
      wavesCleared: this.wavesCleared,
      kills: this.kills,
      lives: this.lives,
      shield: this.shield,
      victory,
      elapsed: this.elapsed,
      difficulty: this.level.difficulty,
    });

    achievements.runSet('score', score);
    achievements.bumpMax('bestScore', score);

    // 自定义地图**不计入排行榜**。
    //
    // 参数虽然是按路径长度自动推算的，但公式没法覆盖玩家能画出的所有
    // 形状，一张精心设计的自定义图完全可能比内置关卡好刷分。与其事后
    // 打补丁，不如直接不参与排名 —— 分数照算照显示，只是不进榜单。
    const ranked = this.level.custom !== true;

    const submitted = ranked
      ? this.leaderboard.submit({
        levelId: this.levelId,
        levelName: this.level.name,
        wavesCleared: this.wavesCleared,
        totalWaves: this.waveSystem.totalWaves,
        kills: this.kills,
        lives: this.lives,
        shield: this.shield,
        towers: this.towers.length,
        elapsed: this.elapsed,
        difficulty: this.level.difficulty,
        victory,
      })
      : { rank: 0, isBest: false, onBoard: false };

    // 立刻评估一次成就，保证结算界面看到的是最终状态
    achievements.evaluate();

    // 结算时立即落盘：累计统计走防抖写入，这里确保切到结算界面前已持久化
    achievements.flush();

    this.lastRunResult = {
      score,
      rank: submitted.rank,
      isBest: submitted.isBest,
      onBoard: submitted.onBoard,
      ranked,
      levelId: this.levelId,
      levelName: this.level.name,
      victory,
    };

    this.hooks.onRunFinalized?.(this.lastRunResult);
    return this.lastRunResult;
  }

  /* ===================================================
   * 内部：清理与同步
   * =================================================== */

  _cleanup() {
    // 敌人：移除已死亡 / 已到达基地的
    const enemies = this.enemies;
    let w = 0;
    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (e.alive && !e.reachedBase) enemies[w++] = e;
    }
    enemies.length = w;

    // 投射物
    const projectiles = this.projectiles;
    w = 0;
    for (let i = 0; i < projectiles.length; i++) {
      const p = projectiles[i];
      if (!p.dead) projectiles[w++] = p;
    }
    projectiles.length = w;
  }

  _emitLives(force) {
    if (force || this.lives !== this._lastLives) {
      this._lastLives = this.lives;
      this.hooks.onLivesChange?.(this.lives);
    }
  }

  /** 只在数值真正变化时通知 UI，避免每帧无意义的 DOM 写入 */
  _syncHud(force) {
    const total = this.waveSystem.totalWaves;
    const current = this.waveSystem.currentWave;

    if (force || current !== this._lastWave) {
      this._lastWave = current;
      this.hooks.onWaveChange?.(current, total);
    }

    const prep = this.waveSystem.isPreparing
      ? Math.ceil(this.waveSystem.prepRemaining)
      : -1;

    if (force || prep !== this._lastPrep) {
      this._lastPrep = prep;
      this.hooks.onPrepChange?.(prep, this.waveSystem.nextIsBoss);
    }
  }

  /* ===================================================
   * 内部：绘制辅助
   * =================================================== */

  _drawTileHighlight(ctx) {
    const tile = this.hoverTile;
    if (!tile) return;

    const { col, row } = tile;
    if (col < 0 || row < 0 || col >= this.map.cols || row >= this.map.rows) return;

    // 只有建造模式才需要高亮可建造性
    if (!this.buildType) return;

    const ok = this.map.isBuildable(col, row) && !this.towerAt(col, row);
    const x = col * TILE_SIZE;
    const y = row * TILE_SIZE;

    ctx.save();
    ctx.fillStyle = ok ? 'rgba(0, 255, 255, 0.16)' : 'rgba(255, 51, 102, 0.16)';
    ctx.fillRect(x, y, TILE_SIZE, TILE_SIZE);

    ctx.strokeStyle = ok ? 'rgba(0, 255, 255, 0.85)' : 'rgba(255, 51, 102, 0.85)';
    ctx.lineWidth = 2;
    ctx.strokeRect(x + 1, y + 1, TILE_SIZE - 2, TILE_SIZE - 2);
    ctx.restore();
  }

  _drawBuildGhost(ctx) {
    if (!this.buildType) return;

    const tile = this.hoverTile;
    if (!tile) return;

    const { col, row } = tile;
    if (col < 0 || row < 0 || col >= this.map.cols || row >= this.map.rows) return;

    const ghost = this._getGhostTower();
    if (!ghost) return;

    const ok = this.map.isBuildable(col, row) && !this.towerAt(col, row);

    ghost.col = col;
    ghost.row = row;
    ghost.x = (col + 0.5) * TILE_SIZE;
    ghost.y = (row + 0.5) * TILE_SIZE;

    if (ok) ghost.drawRange(ctx);
    ghost.draw(ctx, { preview: true, invalid: !ok });
  }

  /** 建造预览用的原型塔（按类型缓存，避免每帧新建对象） */
  _getGhostTower() {
    if (!this.buildType) return null;

    if (!this._ghost || this._ghostType !== this.buildType) {
      try {
        this._ghost = createTower(this.buildType, 0, 0);
        this._ghostType = this.buildType;
      } catch {
        this._ghost = null;
        this._ghostType = null;
      }
    }

    return this._ghost;
  }

  /**
   * 指向型技能的瞄准圈。
   * 桌面端跟随鼠标；移动端没有 hover，就把瞄准圈画在地图中心提示玩家点击。
   */
  _drawSkillReticle(ctx) {
    if (!this.pendingSkill) return;

    const skill = this.skills.getSkill(this.pendingSkill);
    if (!skill) return;

    const g = balanceConfig.getGlobal();
    const reticleR = Number.isFinite(g.orbitalRadius) ? g.orbitalRadius : ORBITAL_RADIUS;

    const pos = this.hoverWorld || { x: this.map.width / 2, y: this.map.height / 2 };
    const pulse = 0.5 + 0.5 * Math.sin(this.elapsed * 4);

    ctx.save();
    ctx.translate(pos.x, pos.y);

    // 作用范围
    ctx.beginPath();
    ctx.arc(0, 0, reticleR, 0, Math.PI * 2);
    ctx.fillStyle = `${skill.color}1f`;
    ctx.fill();

    ctx.setLineDash([8, 8]);
    ctx.strokeStyle = skill.color;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.setLineDash([]);

    // 十字准星
    ctx.strokeStyle = `${skill.color}cc`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(-16, 0);
    ctx.lineTo(-5, 0);
    ctx.moveTo(5, 0);
    ctx.lineTo(16, 0);
    ctx.moveTo(0, -16);
    ctx.lineTo(0, -5);
    ctx.moveTo(0, 5);
    ctx.lineTo(0, 16);
    ctx.stroke();

    // 中心脉冲
    ctx.beginPath();
    ctx.arc(0, 0, 4 + pulse * 5, 0, Math.PI * 2);
    ctx.strokeStyle = skill.color;
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.restore();
  }
}
